import { safeUrl } from "@crm-src/lib/sanitize"
import { socialFooterLinksHtml } from "@crm-src/lib/email-social"
import type { TenantInfo, WebinarSection } from "./stock-images"

// Editor-side preview of the footer that mirrors the email HTML output. The
// website link goes through `safeUrl` here so the editor preview can't be
// abused by a hostile tenant config; the email-side `footerToHtml` uses the
// existing inline protocol-prepend logic to remain byte-identical with the
// original implementation.
interface FooterSectionProps {
  section: WebinarSection
  tenantName: string
  tenantInfo?: TenantInfo
  year: number
}

export function FooterSection({
  section,
  tenantName,
  tenantInfo,
  year,
}: FooterSectionProps) {
  return (
    <div
      key={section.id}
      style={{
        backgroundColor: '#2d3748',
        padding: '17px 40px',
        color: '#ffffff',
        borderTop: '4px solid #6B8E23'
      }}
    >
      <table width="100%" cellPadding="0" cellSpacing="0">
        <tbody>
        <tr>
          <td style={{ textAlign: 'center', paddingBottom: '20px' }}>
            <h3 style={{ color: '#8FBC8F', fontSize: '18px', margin: '0 0 15px 0', fontWeight: 'bold' }}>
              Contact Us
            </h3>
          </td>
        </tr>
        <tr>
          <td>
            <table width="100%" cellPadding="0" cellSpacing="0">
              <tbody>
              <tr>
                <td style={{ width: '50%', verticalAlign: 'top', paddingRight: '20px' }}>
                  <p style={{ color: '#ffffff', fontSize: '16px', margin: '0 0 5px 0', fontWeight: 'bold' }}>
                    {tenantInfo?.name || tenantName}
                  </p>
                  {tenantInfo?.address && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      {tenantInfo.address}
                    </p>
                  )}
                  {(tenantInfo?.city || tenantInfo?.state || tenantInfo?.postal_code) && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      {[tenantInfo?.city, tenantInfo?.state, tenantInfo?.postal_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {tenantInfo?.country && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      {tenantInfo.country}
                    </p>
                  )}
                </td>
                <td style={{ width: '50%', verticalAlign: 'top', paddingLeft: '20px' }}>
                  {tenantInfo?.phone && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      Phone: {tenantInfo.phone}
                    </p>
                  )}
                  {tenantInfo?.email && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      Email: <a href={`mailto:${tenantInfo.email}`} style={{ color: '#8FBC8F', textDecoration: 'none' }}>{tenantInfo.email}</a>
                    </p>
                  )}
                  {tenantInfo?.website && (
                    <p style={{ color: '#cbd5e0', fontSize: '14px', margin: '5px 0' }}>
                      Web: <a href={safeUrl(tenantInfo.website)} style={{ color: '#8FBC8F', textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">{tenantInfo.website}</a>
                    </p>
                  )}
                </td>
              </tr>
              </tbody>
            </table>
          </td>
        </tr>
        <tr>
          <td style={{ textAlign: 'center', paddingTop: '30px', borderTop: '1px solid #4a5568', marginTop: '20px' }}>
            {/* One fixed follow-us block (LinkedIn + YouTube CTA buttons — see email-social.ts).
                Rendered from the same HTML the email emits so the preview stays WYSIWYG. */}
            <div dangerouslySetInnerHTML={{ __html: socialFooterLinksHtml({ theme: 'dark', marginStyle: 'margin:0 0 12px 0;' }) }} />
            <p style={{ color: '#a0aec0', fontSize: '12px', margin: '3px 0 0 0' }}>
              © {year} {tenantInfo?.name || tenantName}. All rights reserved.
            </p>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  )
}

// Build the footer rows of the rendered email HTML. Mirrors the editor preview
// above but emits inline-styled HTML strings for email-client compatibility.
// _section is unused — kept for signature parity with the other XxxToHtml
// helpers so the orchestrator can dispatch uniformly.
export function footerToHtml(
  _section: WebinarSection,
  tenantName: string,
  tenantInfo: TenantInfo | undefined,
  year: number
): string {
  return `
          <!-- Footer -->
          <tr>
            <td class="mobile-pad" bgcolor="#2d3748" style="background-color: #2d3748; padding: 17px 40px; color: #ffffff; border-top: 4px solid #6B8E23;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td align="center" style="text-align: center; padding-bottom: 20px;">
                    <h3 style="color: #8FBC8F; font-size: 18px; margin: 0 0 15px 0; font-weight: bold; font-family: Arial, Helvetica, sans-serif; line-height: 24px;">
                      Contact Us
                    </h3>
                  </td>
                </tr>
                <tr>
                  <td>
                    <table width="100%" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td class="mobile-stack mobile-center" width="50%" style="width: 50%; vertical-align: top; padding-right: 20px;" valign="top">
                          <p style="color: #ffffff; font-size: 16px; margin: 0 0 5px 0; font-weight: bold; font-family: Arial, Helvetica, sans-serif; line-height: 22px;">
                            ${tenantInfo?.name || tenantName}
                          </p>
                          ${tenantInfo?.address ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0; font-family: Arial, Helvetica, sans-serif; line-height: 20px;">
                            ${tenantInfo.address}
                          </p>
                          ` : ''}
                          ${tenantInfo?.city || tenantInfo?.state || tenantInfo?.postal_code ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0; font-family: Arial, Helvetica, sans-serif; line-height: 20px;">
                            ${[tenantInfo?.city, tenantInfo?.state, tenantInfo?.postal_code].filter(Boolean).join(', ')}
                          </p>
                          ` : ''}
                          ${tenantInfo?.country ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0; font-family: Arial, Helvetica, sans-serif; line-height: 20px;">
                            ${tenantInfo.country}
                          </p>
                          ` : ''}
                        </td>
                        <td class="mobile-stack mobile-center" width="50%" style="width: 50%; vertical-align: top; padding-left: 20px;" valign="top">
                          ${tenantInfo?.phone ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0; font-family: Arial, Helvetica, sans-serif; line-height: 20px;">
                            Phone: ${tenantInfo.phone}
                          </p>
                          ` : ''}
                          ${tenantInfo?.email ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0; font-family: Arial, Helvetica, sans-serif; line-height: 20px;">
                            Email: <a href="mailto:${tenantInfo.email}" style="color: #8FBC8F; text-decoration: none; font-family: Arial, Helvetica, sans-serif;">${tenantInfo.email}</a>
                          </p>
                          ` : ''}
                          ${tenantInfo?.website ? `
                          <p style="color: #cbd5e0; font-size: 14px; margin: 5px 0; font-family: Arial, Helvetica, sans-serif; line-height: 20px;">
                            Web: <a href="${tenantInfo.website.startsWith('http') ? tenantInfo.website : 'https://' + tenantInfo.website}" style="color: #8FBC8F; text-decoration: none; font-family: Arial, Helvetica, sans-serif;" target="_blank">${tenantInfo.website}</a>
                          </p>
                          ` : ''}
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td align="center" style="text-align: center; padding-top: 30px; border-top: 1px solid #4a5568;">
                    ${socialFooterLinksHtml({ theme: "dark", marginStyle: "margin:15px 0 10px 0;" })}
                    <p style="color: #a0aec0; font-size: 12px; margin: 6px 0 0 0; font-family: Arial, Helvetica, sans-serif; line-height: 18px;">
                      &copy; ${year} ${tenantInfo?.name || tenantName}. All rights reserved.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          `
}
